import { listLikesByComments } from '@/features/comunicacao/api/commentLikesApi';
import { createComment as createAppComment } from '@/features/comunicacao/api/commentsApi';
import { ensureThread as ensureThreadRpc } from '@/features/comunicacao/api/commentThreadsApi';
import { appCommentsService, type AppCommentThread, type RecursoTipo } from '@/features/comunicacao/services/appCommentsService';
import { supabase } from '@/features/sistema/services/supabaseClient';

export type ComentarioTipo = 'texto' | 'foto' | 'arquivo';
type UsuarioJoinRow = { id: string; nome: string | null; email: string | null };

function resolveUsuarioJoin(input?: UsuarioJoinRow | UsuarioJoinRow[] | null) {
  if (Array.isArray(input)) {
    return input[0] ?? null;
  }
  return input ?? null;
}

export type Comentario = {
  id: string;
  thread_id: string;
  conteudo: string;
  tipo: ComentarioTipo;
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  mencoes?: string[] | null;
  usuarios_mencionados?: Array<{ id: string; nome: string; email: string | null }>;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  created_by?: string | null;
  criado_por?: { id: string; nome: string; email: string | null };
  created_by_id: string | null;
  editado?: boolean;
  fixado?: boolean;
  ordem_fixacao?: number | null;
  curtidas_count?: number;
  curtido_por_mim?: boolean;
};

type FetchOptions = {
  currentUsuarioId?: string | null;
};

const RECURSO: RecursoTipo = 'cronograma_subetapa';

async function ensureCronogramaThread(
  obraId: string,
  subetapaId: string,
  opts: { userId?: string | null } = {},
): Promise<AppCommentThread> {
  let thread = await appCommentsService.getThreadByResource(RECURSO, subetapaId);
  if (thread) return thread;

  const ensuredId = await ensureThreadRpc({
    obraId,
    recurso: RECURSO,
    recursoId: subetapaId,
    userId: opts.userId ?? null,
  });

  if (!ensuredId) {
    throw new Error(`Não foi possível garantir thread para subetapa ${subetapaId}`);
  }

  thread = await appCommentsService.getThreadByResource(RECURSO, subetapaId);
  if (!thread) {
    throw new Error(`Thread criada não encontrada para subetapa ${subetapaId}`);
  }
  return thread;
}

async function fetchCommentsByThread(threadId: string, opts: FetchOptions = {}): Promise<Comentario[]> {
  const { data, error } = await supabase
    .from('app_comments')
    .select(`
      id,
      thread_id,
      conteudo,
      tipo,
      arquivo_url,
      arquivo_nome,
      mencoes,
      created_at,
      updated_at,
      deleted_at,
      fixado,
      ordem_fixacao,
      created_by,
      criado_por:usuarios!app_comments_created_by_fkey ( id, nome, email )
    `)
    .eq('thread_id', threadId)
    .is('deleted_at', null)
    .order('fixado', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<{
    id: string;
    thread_id: string;
    conteudo: string | null;
    tipo: ComentarioTipo | null;
    arquivo_url: string | null;
    arquivo_nome: string | null;
    mencoes: string[] | null;
    created_at: string;
    updated_at: string | null;
    deleted_at: string | null;
    fixado: boolean | null;
    ordem_fixacao: number | null;
    created_by: string | null;
    criado_por?: UsuarioJoinRow | UsuarioJoinRow[] | null;
  }>;

  const ids = rows.map((row) => row.id);
  const likesById = new Map<string, number>();
  const likedByMe = new Set<string>();

  if (ids.length > 0) {
    const likes = await listLikesByComments(ids);
    likes.forEach((like) => {
      likesById.set(like.comment_id, (likesById.get(like.comment_id) ?? 0) + 1);
      if (opts.currentUsuarioId && like.usuario_id === opts.currentUsuarioId) {
        likedByMe.add(like.comment_id);
      }
    });
  }

  const mentionIds = new Set<string>();
  rows.forEach((row) => {
    (row.mencoes ?? []).forEach((id) => {
      if (typeof id === 'string' && id.trim()) {
        mentionIds.add(id);
      }
    });
  });

  const mentionMap = new Map<string, { id: string; nome: string; email: string | null }>();
  if (mentionIds.size > 0) {
    const { data: mentionUsers, error: mentionError } = await supabase
      .from('usuarios')
      .select('id, nome, email')
      .in('id', Array.from(mentionIds));
    if (!mentionError) {
      (mentionUsers ?? []).forEach((usuario: any) => {
        mentionMap.set(usuario.id, {
          id: usuario.id,
          nome: usuario.nome ?? 'Usuário',
          email: usuario.email ?? null,
        });
      });
    }
  }

  return rows.map((row) => {
    const createdById = row.created_by ?? null;
    const autor = resolveUsuarioJoin(row.criado_por);
    const createdByInfo = autor
      ? {
          id: autor.id,
          nome: autor.nome ?? 'Usuario',
          email: autor.email ?? null,
        }
      : createdById
      ? {
          id: createdById,
          nome: 'Usuario',
          email: null,
        }
      : undefined;

    const mencoes = Array.isArray(row.mencoes) ? row.mencoes.filter((id) => typeof id === 'string' && id.trim()) : null;
    const usuariosMencionados = mencoes
      ? mencoes
          .map((id) => mentionMap.get(id))
          .filter((value): value is { id: string; nome: string; email: string | null } => !!value)
      : [];

    const editado = Boolean(row.updated_at && row.updated_at !== row.created_at);

    return {
      id: row.id,
      thread_id: row.thread_id,
      conteudo: row.conteudo ?? '',
      tipo: (row.tipo ?? 'texto') as ComentarioTipo,
      arquivo_url: row.arquivo_url,
      arquivo_nome: row.arquivo_nome,
      mencoes,
      usuarios_mencionados: usuariosMencionados,
      created_at: row.created_at,
      updated_at: row.updated_at ?? undefined,
      deleted_at: row.deleted_at ?? undefined,
      created_by: createdById,
      criado_por: createdByInfo,
      created_by_id: createdById,
      editado,
      fixado: row.fixado ?? false,
      ordem_fixacao: row.ordem_fixacao ?? null,
      curtidas_count: likesById.get(row.id) ?? 0,
      curtido_por_mim: opts.currentUsuarioId ? likedByMe.has(row.id) : undefined,
    };
  });
}

export async function getSubetapaComentariosComMencoes(
  subetapaId: string,
  opts: FetchOptions & { obraId?: string | null } = {},
): Promise<Comentario[]> {
  if (!subetapaId) return [];

  let thread = await appCommentsService.getThreadByResource(RECURSO, subetapaId);
  if (!thread && opts.obraId) {
    try {
      thread = await ensureCronogramaThread(opts.obraId, subetapaId);
    } catch (error) {
      console.error('Erro ao garantir thread do cronograma', error);
    }
  }

  if (!thread) return [];

  return fetchCommentsByThread(thread.id, opts);
}

export async function getSubetapasComentariosCounts(subetapaIds: string[]): Promise<Record<string, number>> {
  if (!subetapaIds.length) return {};

  const { data, error } = await supabase
    .from('app_comments')
    .select(
      `
      id,
      thread:app_comment_threads!inner(
        recurso,
        recurso_id
      )
    `,
    )
    .in('thread.recurso_id', subetapaIds)
    .eq('thread.recurso', RECURSO)
    .is('deleted_at', null);

  if (error) {
    console.error('❌ Erro ao buscar contagem de comentários:', error);
    return {};
  }

  const counts: Record<string, number> = {};
  (data ?? []).forEach((row: any) => {
    const key = row.thread?.recurso_id;
    if (!key) return;
    counts[key] = (counts[key] ?? 0) + 1;
  });
  return counts;
}

export async function createSubetapaComentario(input: {
  subetapa_id: string;
  conteudo: string;
  tipo?: ComentarioTipo;
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  mencoes?: string[];
  obra_id: string;
  author_usuario_id: string;
  thread_id?: string | null;
}) {
  let threadId = input.thread_id ?? null;

  if (!threadId) {
    const thread = await ensureCronogramaThread(input.obra_id, input.subetapa_id, {
      userId: input.author_usuario_id,
    });
    threadId = thread.id;
  }
  if (!threadId) {
    throw new Error('Não foi possível obter a thread do cronograma');
  }

  const created = await createAppComment({
    threadId: threadId,
    conteudo: input.conteudo,
    tipo: input.tipo ?? 'texto',
    arquivoUrl: input.arquivo_url ?? null,
    arquivoNome: input.arquivo_nome ?? null,
    mencoes: input.mencoes?.length ? input.mencoes : null,
    createdBy: input.author_usuario_id,
  });

  const comentarioId = created.id;
  const urlAcao = `/obras/${input.obra_id}/cronograma?subetapa=${input.subetapa_id}&comentario=${comentarioId}`;

  if (input.mencoes?.length) {
    const notificacoes = input.mencoes
      .filter((uid) => uid && uid !== input.author_usuario_id)
      .map((uid) => ({
        tipo: 'mencao',
        titulo: 'Você foi mencionado',
        mensagem: input.conteudo.slice(0, 160),
        usuario_id: uid,
        obra_id: input.obra_id,
        comentario_id: comentarioId,
        url_acao: urlAcao,
        dados: { origem: 'cronograma', subetapa_id: input.subetapa_id },
      }));
    if (notificacoes.length) await supabase.from('notificacoes').insert(notificacoes);
  }

  const { data: sub } = await supabase
    .from('subetapas_obra')
    .select('responsavel_id')
    .eq('id', input.subetapa_id)
    .maybeSingle();

  if (sub?.responsavel_id && sub.responsavel_id !== input.author_usuario_id) {
    await supabase.from('notificacoes').insert({
      tipo: 'novo_comentario',
      titulo: 'Nova atualização na subetapa',
      mensagem: input.conteudo.slice(0, 160),
      usuario_id: sub.responsavel_id,
      obra_id: input.obra_id,
      comentario_id: comentarioId,
      url_acao: urlAcao,
      dados: { origem: 'cronograma', subetapa_id: input.subetapa_id },
    });
  }

  return comentarioId;
}

function normalizarTipoAnexo(tipo?: string | null): ComentarioTipo {
  if (!tipo) return 'arquivo';
  if (tipo === 'foto' || tipo === 'imagem') return 'foto';
  if (tipo === 'video') return 'arquivo';
  return tipo === 'texto' ? 'texto' : 'arquivo';
}

export async function anexarArquivosAoComentario({
  comentarioId,
  threadId,
  anexos,
  usuarioId,
}: {
  comentarioId: string;
  threadId?: string | null;
  anexos: Array<{ url: string; nome: string; tipo: ComentarioTipo }>;
  usuarioId?: string | null;
}) {
  if (!anexos.length) return;

  const [first, ...rest] = anexos;

  await supabase
    .from('app_comments')
    .update({
      arquivo_url: first.url,
      arquivo_nome: first.nome,
      tipo: normalizarTipoAnexo(first.tipo),
    })
    .eq('id', comentarioId);

  if (!threadId || !rest.length) return;

  for (const extra of rest) {
    await createAppComment({
      threadId,
      conteudo: extra.nome ?? '',
      tipo: normalizarTipoAnexo(extra.tipo),
      arquivoUrl: extra.url,
      arquivoNome: extra.nome ?? null,
      createdBy: usuarioId ?? null,
    });
  }
}

export async function updateSubetapaComentarioCompleto(id: string, conteudo: string, editorUsuarioId: string) {
  const { error } = await supabase
    .from('app_comments')
    .update({
      conteudo,
      editado: true,
      updated_by: editorUsuarioId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
}

export async function deleteSubetapaComentario(id: string) {
  const { error } = await supabase
    .from('app_comments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

export async function podeEditarComentario(comentarioId: string, usuarioId: string) {
  const { data, error } = await supabase
    .from('app_comments')
    .select('created_by')
    .eq('id', comentarioId)
    .maybeSingle();

  if (error) return false;
  return !!data && data.created_by === usuarioId;
}

export async function ensureThread(obraId: string, subetapaId: string, opts: { userId?: string | null } = {}) {
  return ensureCronogramaThread(obraId, subetapaId, opts);
}

export async function listComments(threadId: string, opts: FetchOptions = {}) {
  return fetchCommentsByThread(threadId, opts);
}

export async function addComment(params: {
  threadId: string;
  texto: string;
  mencoes?: string[];
  arquivoUrl?: string | null;
  arquivoNome?: string | null;
  subetapaId?: string;
  obraId?: string;
  authorUsuarioId?: string | null;
}) {
  if (!params.threadId) {
    throw new Error('threadId é obrigatório para adicionar comentários');
  }

  await createAppComment({
    threadId: params.threadId,
    conteudo: params.texto,
    tipo: params.arquivoUrl ? 'arquivo' : 'texto',
    arquivoUrl: params.arquivoUrl ?? null,
    arquivoNome: params.arquivoNome ?? null,
    mencoes: params.mencoes?.length ? params.mencoes : null,
    createdBy: params.authorUsuarioId ?? null,
  });
}
